import { ReturnBanner } from "@/components/return-banner";
import { useRouter } from "@/lib/navigation";
import { useReturnTo, withReturnTo } from "@/lib/return-to";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Database, Plus, Trash2 } from "lucide-react";
import { Link } from "@/components/ui/link";
import * as React from "react";

import { KbRegisterDialog } from "@/components/authoring/kb-register-dialog";
import { KbStatusBadge } from "@/components/authoring/kb-status-badge";
import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Button,
  Card,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { deleteKnowledgeBase } from "@/lib/api/endpoints";
import { useKnowledgeBases } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { KnowledgeBaseView } from "@/types/api";

export default function KnowledgeBasesPage() {
  const returnTo = useReturnTo();
  const router = useRouter();

  const { data, isLoading, isError, error, refetch } = useKnowledgeBases();
  const qc = useQueryClient();

  const [registerOpen, setRegisterOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<KnowledgeBaseView | undefined>();

  const del = useMutation({
    mutationFn: (slug: string) => deleteKnowledgeBase(slug),
    onSuccess: async () => {
      toast.success("Knowledge base deleted.");
      await qc.invalidateQueries({ queryKey: qk.knowledgeBases });
      setToDelete(undefined);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div>
      {returnTo ? <ReturnBanner to={returnTo} noun="a knowledge base" /> : null}
      <PageHeader
        title="Knowledge Base"
        description="Ground your agents in your own data — connect a SQL database and query it in natural language."
        actions={
          <Button onClick={() => setRegisterOpen(true)}>
            <Plus className="h-4 w-4" />
            Register knowledge base
          </Button>
        }
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          title="No knowledge bases yet"
          hint="Connect a SQL database, embed its schema, and ask questions in plain English."
          action={
            <Button variant="outline" onClick={() => setRegisterOpen(true)}>
              <Database className="h-4 w-4" />
              Register your first
            </Button>
          }
        />
      ) : (
        <Card className="p-4">
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Status</TH>
                <TH>Source</TH>
                <TH>Domain</TH>
                <TH>Objects</TH>
                <TH>Agents</TH>
                <TH className="w-16 text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {(data ?? []).map((kb) => (
                <TR key={kb.id}>
                  <TD className="font-medium">
                    <Link href={`/knowledge-bases/${kb.slug}`} className="hover:underline">
                      {kb.name}
                    </Link>
                    <div className="font-mono text-xs text-muted-foreground">{kb.slug}</div>
                  </TD>
                  <TD>
                    <KbStatusBadge status={kb.status} />
                  </TD>
                  <TD className="uppercase text-muted-foreground">{kb.source_type}</TD>
                  <TD className="text-muted-foreground">{kb.domain_slug}</TD>
                  <TD className="tabular-nums">{kb.object_count}</TD>
                  <TD className="tabular-nums">{kb.attached_count}</TD>
                  <TD className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete"
                      onClick={() => setToDelete(kb)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <KbRegisterDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        onCreated={
          returnTo
            ? (kb) =>
                // Straight to the new KB so its schema can be ingested — it
                // cannot answer anything until that runs. `return_to` rides
                // along so the trip back is still one click from there.
                router.push(withReturnTo(`/knowledge-bases/${kb.slug}`, returnTo))
            : undefined
        }
      />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(undefined)}
        title={`Delete knowledge base “${toDelete?.name}”?`}
        description="This removes the KB and its vector collection. Detach it from any agent versions first if it's in use."
        pending={del.isPending}
        onConfirm={() => toDelete && del.mutate(toDelete.slug)}
      />
    </div>
  );
}
